import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Stack, SwipeableDrawer, Typography } from '@mui/material';

/**
 * A mobile-specific swipeable drawer component that provides a bottom-up overlay for displaying content.
 * It is customizable with a title and can contain any children elements. The drawer's style is
 * adapted from the Material-UI theme, ensuring consistent design with the rest of the application.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode|React.ReactNode[]} props.children - The content to be displayed inside the drawer.
 * @param {boolean} [props.open=false] - Controls the visibility of the drawer.
 * @param {string} [props.title] - An optional title displayed at the top of the drawer.
 * @param {function} props.onOpen - Callback function triggered when the drawer is swiped open.
 * @param {function} props.onClose - Callback function triggered when the drawer is swiped closed or when an area outside the drawer is clicked.
 * @returns {JSX.Element} A SwipeableDrawer component customized for mobile usage, with optional title and children.
 */
const MSwipeableDrawerMobile = (props) => {
  const { children, open = false, title, onOpen, onClose } = props;
  const {
    shape,
    palette: { divider }
  } = useTheme();

  return (
    <SwipeableDrawer anchor="bottom" sx={styleDrawer(shape)} open={open} onClose={onClose} onOpen={onOpen}>
      <Stack id="swipeable-drawer-mobile-puller-wrapper" sx={stylePullerWrapper(divider)}>
        {/* Puller visual element */}
      </Stack>

      {title && <Typography variant="subtitle1">{title}</Typography>}

      {[children]}
    </SwipeableDrawer>
  );
};

const styleDrawer = (shape) => ({
  zIndex: 9999, // ? Default zIndex for MUI modals is 1200
  '.MuiDrawer-paper': {
    maxHeight: '85vh',
    p: 2,
    pb: 6, // Prevent from overriding bottom puller on iOS
    borderRadius: shape.borderRadiusMd,
    borderBottomLeftRadius: 0, //! no support for theme.shape.borderRadiusMd on specific angles
    borderBottomRightRadius: 0
  }
});

const stylePullerWrapper = (divider) => ({
  alignItems: 'center',
  pb: 3,
  ':before': {
    content: "''",
    height: 4,
    width: 40,
    backgroundColor: divider
  }
});

MSwipeableDrawerMobile.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  open: PropTypes.bool,
  title: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default MSwipeableDrawerMobile;
